/**
 * Data access for the Task Manager feature.
 *
 * Talks to the dedicated Task Manager AppSync API (see TaskManagerStack). Every
 * operation from section 5.2 of the TDD takes a single JSON `input` (AWSJSON)
 * and returns JSON (AWSJSON); the resolver Lambda owns shape/validation.
 *
 * The "Include Apps" multiselect is populated from the existing Plus AppSync
 * `getAppsV2` query (the same source the Marketplace uses).
 */

import { callTaskManagerGraphQLDirect, callPlusGraphQLDirect } from "src/utilities/AWSServices";
import { createAuditLog, fetchApps } from "src/Ateam-BI/Templates/Utils/AppActivityUtils";
import {
  createPreference,
  deletePreference,
  getPreference,
  listPreferenceByPreferenceType,
  updatePreference,
} from "src/utilities/AppSyncPlusAPIs";

/**
 * Task Manager AppSync ops return an AWSJSON scalar, which the API serializes as
 * a JSON string. Normalize to a JS object/array.
 */
const parseResult = (response) => {
  const dataKey = Object.keys(response.data || {})[0];
  let raw = response.data ? response.data[dataKey] : null;
  if (raw == null) return null;
  // The response VTL runs $util.toJson on a payload the Lambda already
  // json.dumps'd, so the AWSJSON scalar can come back double-encoded. Parse
  // repeatedly until we reach a non-string (object/array), tolerating both
  // single- and double-encoded payloads.
  while (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (e) {
      break; // Not JSON (e.g. a plain string value) — return as-is.
    }
  }
  return raw;
};

const runTaskManagerOp = async (opName, isMutation, input) => {
  const keyword = isMutation ? "mutation" : "query";
  // The schema declares `input: AWSJSON!` (non-null), so the variable must be
  // declared non-null too and always carry a value (default to an empty object).
  const query = `${keyword} ${opName}($input: AWSJSON!) {
    ${opName}(input: $input)
  }`;
  const response = await callTaskManagerGraphQLDirect(query, {
    input: JSON.stringify(input == null ? {} : input),
  });
  return parseResult(response);
};

/* ---- Section 5.2 operations ---- */

export const listDashboards = (input = {}) => runTaskManagerOp("listDashboards", false, input);

export const getDashboard = (input) => runTaskManagerOp("getDashboard", false, input);

export const getServices = (input) => runTaskManagerOp("getServices", false, input);

export const getTrackingHistory = (input) => runTaskManagerOp("getTrackingHistory", false, input);

export const createDashboard = (input) => runTaskManagerOp("createDashboard", true, input);

export const updateDashboard = (input) => runTaskManagerOp("updateDashboard", true, input);

export const updateProcessLiteViaTaskManager = (input) =>
  runTaskManagerOp("updateProcessLiteViaTaskManager", true, input);

export const updateFlexicoTemplateViaTaskManager = (input) =>
  runTaskManagerOp("updateFlexicoTemplateViaTaskManager", true, input);

export const updateLogicoProcessLiteViaTaskManager = (input) =>
  runTaskManagerOp("updateLogicoProcessLiteViaTaskManager", true, input);

/* ---- AdHoc tasks (confirmation-only deliverables) ----
 * Associated with an entity via { entity_type, entity_id } - entity_type is a
 * name in the adhoc_entity_types lookup (DASHBOARD, APP, ...), entity_id is the
 * id within that type (e.g. a dashboard_id when entity_type = DASHBOARD). */

// List every AdHoc task for an entity (entityType name + id within that type).
export const listAdhocTasks = (entityType, entityId) =>
  runTaskManagerOp("listAdhocTasks", false, {
    entity_type: `${entityType}`,
    entity_id: `${entityId}`,
  });

// Fetch a single AdHoc task (with its uploaders/reviewers).
export const getAdhocTask = (taskId) =>
  runTaskManagerOp("getAdhocTask", false, { task_id: `${taskId}` });

// Create an AdHoc task. input: { entity_type, entity_id, task_name, description,
// uploaders: [{alias,type}], reviewers: [{alias,type,column_index}] }.
export const createAdhocTask = (input) => runTaskManagerOp("createAdhocTask", true, input);

// Update an AdHoc task (task_id required); replaces uploaders/reviewers.
export const updateAdhocTask = (input) => runTaskManagerOp("updateAdhocTask", true, input);

// Delete an AdHoc task.
export const deleteAdhocTask = (taskId) =>
  runTaskManagerOp("deleteAdhocTask", true, { task_id: `${taskId}` });

// Confirm ("mark done") an AdHoc task for a quarter / WB interval.
// input: { task_id, entity_type, entity_id, quarter, tracking_type }.
export const confirmAdhocTask = (input) => runTaskManagerOp("confirmAdhocTask", true, input);

/* ---- Custom Task Manager columns via shared GenericPreference API ---- */

const TASK_MANAGER_COLUMN_PREFERENCE_TYPE = "TASK_MANAGER_COLUMNS";
const TASK_MANAGER_COLUMN_LABEL = "COLUMNS";
const TASK_MANAGER_COLUMN_ASSOCIATION_TYPE = "TASK_MANAGER_DASHBOARD_TAB";

const taskManagerColumnPreferenceId = (dashboardId, tabId) =>
  `task_manager#dashboard#${dashboardId}#tab#${tabId}#columns`;

const preferenceFailure = (reject) => (response) =>
  reject(new Error(response?.errors?.[0]?.message || "GenericPreference request failed"));

const parsePreferenceInformation = (information, fallback) => {
  let parsed = information;
  while (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      return fallback;
    }
  }
  return parsed == null ? fallback : parsed;
};
const parsePreferenceColumns = (information) => {
  const parsed = parsePreferenceInformation(information, []);
  if (!Array.isArray(parsed)) return [];

  const seenColumns = [];
  return parsed.reduce((columns, column, index) => {
    const name = typeof column?.name === "string" ? column.name.trim() : "";
    const type = typeof column?.type === "string" ? column.type.toUpperCase() : "";
    const field = typeof column?.field === "string" ? column.field.trim() : "";
    const identity = type === "DEFAULT" ? `default:${field}` : `custom:${name.toLowerCase()}`;
    if (!name || !type || (type === "DEFAULT" && !field) || seenColumns.includes(identity)) {
      return columns;
    }
    seenColumns.push(identity);

    const order = Number(column.order);
    const normalized = {
      name,
      type,
      is_visible: column.is_visible !== false,
      order: Number.isFinite(order) ? order : index,
    };
    if (type === "DEFAULT") {
      normalized.field = field;
    }
    if (typeof column.due_date_type === "string" && column.due_date_type.trim()) {
      normalized.due_date_type = column.due_date_type.trim();
    }
    if (typeof column.tracking_sub_group === "string" && column.tracking_sub_group.trim()) {
      normalized.tracking_sub_group = column.tracking_sub_group.trim();
    }
    if (type === "DATE") {
      normalized.required_when_triggered =
        column.required_when_triggered === true || column.requiredWhenTriggered === true;
      normalized.include_in_digest =
        column.include_in_digest === true || column.includeInDigest === true;
    }
    if (type === "DROPDOWN" && Array.isArray(column.values)) {
      const values = [...new Set(
        column.values
          .filter((value) => typeof value === "string")
          .map((value) => value.trim())
          .filter(Boolean)
      )];
      if (values.length > 0) normalized.values = values;
    }
    columns.push(normalized);
    return columns;
  }, []);
};

export const getTaskManagerColumnConfig = (dashboardId, tabId = "default") =>
  new Promise((resolve, reject) => {
    const preferenceId = taskManagerColumnPreferenceId(dashboardId, tabId);
    getPreference(
      preferenceId,
      (response) => {
        const preference = response?.data?.getPreference || null;
        resolve({
          exists: !!preference,
          preferenceId,
          columns: parsePreferenceColumns(preference?.information),
        });
      },
      preferenceFailure(reject)
    );
  });

export const saveTaskManagerColumnConfig = ({
  dashboardId,
  tabId = "default",
  columns,
  userId,
  preferenceExists,
}) =>
  new Promise((resolve, reject) => {
    const preferenceId = taskManagerColumnPreferenceId(dashboardId, tabId);
    const normalizedColumns = parsePreferenceColumns(columns);
    const onSuccess = () =>
      resolve({ preferenceId, columns: normalizedColumns, exists: true });
    const onFailure = preferenceFailure(reject);

    if (preferenceExists) {
      updatePreference(
        preferenceId,
        TASK_MANAGER_COLUMN_LABEL,
        userId,
        normalizedColumns,
        onSuccess,
        onFailure
      );
      return;
    }

    createPreference(
      preferenceId,
      TASK_MANAGER_COLUMN_PREFERENCE_TYPE,
      TASK_MANAGER_COLUMN_LABEL,
      `${dashboardId}#${tabId}`,
      TASK_MANAGER_COLUMN_ASSOCIATION_TYPE,
      userId,
      normalizedColumns,
      null,
      onSuccess,
      onFailure
    );
  });

/* ---- Task Manager control-panel settings via GenericPreference ---- */

const TASK_MANAGER_DASHBOARD_ASSOCIATION_TYPE = "TASK_MANAGER_DASHBOARD_ID";
const STATUS_MAPPING_PREFERENCE_TYPE = "STATUS_MAPPING";
const DIGEST_CONFIG_PREFERENCE_TYPE = "DIGEST_CONFIG";
const EMAIL_MAPPING_PREFERENCE_TYPE = "EMAIL_MAPPING";
const EMAIL_MAPPING_ASSOCIATION_TYPE = "AMZ_ALIAS";

const statusMappingPreferenceId = (dashboardId) =>
  `task_manager#dashboard#${dashboardId}#status_mapping`;
const digestConfigPreferenceId = (dashboardId) =>
  `task_manager#dashboard#${dashboardId}#digest_config`;
const emailMappingPreferenceId = (alias) =>
  `task_manager#alias#${alias.trim().toLowerCase()}#email_mapping`;

const getPreferenceItem = (preferenceId) =>
  new Promise((resolve, reject) => {
    getPreference(
      preferenceId,
      (response) => resolve(response?.data?.getPreference || null),
      preferenceFailure(reject)
    );
  });

const listPreferenceItemsByType = (preferenceType) =>
  new Promise((resolve, reject) => {
    listPreferenceByPreferenceType(
      preferenceType,
      (response) => resolve(response?.data?.listPreferenceByPreferenceType?.items || []),
      preferenceFailure(reject)
    );
  });

const upsertPreference = ({
  preferenceId,
  exists,
  preferenceType,
  label,
  associationId,
  associationIdType,
  userId,
  information,
}) =>
  new Promise((resolve, reject) => {
    const onSuccess = () => resolve(preferenceId);
    const onFailure = preferenceFailure(reject);
    if (exists) {
      updatePreference(preferenceId, label, userId, information, onSuccess, onFailure);
      return;
    }
    createPreference(
      preferenceId,
      preferenceType,
      label,
      associationId,
      associationIdType,
      userId,
      information,
      null,
      onSuccess,
      onFailure
    );
  });

const removePreference = (preferenceId) =>
  new Promise((resolve, reject) => {
    deletePreference(
      preferenceId,
      null,
      () => resolve(preferenceId),
      preferenceFailure(reject)
    );
  });

export const getTaskManagerSettings = async (dashboardId) => {
  const [statusPreference, digestPreference, emailPreferences] = await Promise.all([
    getPreferenceItem(statusMappingPreferenceId(dashboardId)),
    getPreferenceItem(digestConfigPreferenceId(dashboardId)),
    listPreferenceItemsByType(EMAIL_MAPPING_PREFERENCE_TYPE),
  ]);

  const statusMappings = parsePreferenceInformation(statusPreference?.information, []);
  const digestConfig = parsePreferenceInformation(digestPreference?.information, {
    enabled: false,
  });
  const emailMappings = emailPreferences
    .filter((item) => item?.associationIdType === EMAIL_MAPPING_ASSOCIATION_TYPE)
    .map((item) => {
      const information = parsePreferenceInformation(item.information, { emails: [] });
      return {
        preferenceId: item.preferenceId,
        alias: item.associationId || "",
        emails: Array.isArray(information?.emails) ? information.emails : [],
      };
    })
    .filter((item) => item.alias);

  return {
    statusMappingExists: !!statusPreference,
    statusMappings: Array.isArray(statusMappings) ? statusMappings : [],
    digestConfigExists: !!digestPreference,
    digestConfig:
      digestConfig && typeof digestConfig === "object" ? digestConfig : { enabled: false },
    emailMappings,
  };
};

const normalizeTaskManagerSettingsPreferences = (settings) => {
  const statusMappings = (settings?.statusMappings || []).map((mapping) => ({
    source_status: mapping.key.trim(),
    display_status: mapping.value.trim(),
    // The existing dropdown exposes SUCCESS / ERROR / WARNING. Persist that
    // selection as the display color/style value until dedicated colors exist.
    color: mapping.status,
  }));
  const digestConfig = { enabled: !!settings?.notifications?.dailyDigest };
  const emailMappingsByAlias = new Map();
  (settings?.emailMappings || []).forEach((mapping) => {
    const alias = mapping?.alias?.trim();
    const emails = (mapping?.emails || [])
      .map((email) => email.trim())
      .filter(Boolean);
    if (!alias || emails.length === 0) return;

    const normalizedAlias = alias.toLowerCase();
    const existing = emailMappingsByAlias.get(normalizedAlias) || {
      alias,
      emails: new Set(),
    };
    emails.forEach((email) => existing.emails.add(email));
    emailMappingsByAlias.set(normalizedAlias, existing);
  });
  const emailMappings = [...emailMappingsByAlias.values()].map((mapping) => ({
    alias: mapping.alias,
    emails: [...mapping.emails],
  }));

  return { statusMappings, digestConfig, emailMappings };
};

const createTaskManagerSettingsAudit = ({ dashboardId, userId, before, after }) =>
  new Promise((resolve, reject) => {
    createAuditLog(
      `TASK_MANAGER#${dashboardId}`,
      `SETTINGS#${Date.now()}`,
      "Task Manager settings preference update",
      JSON.stringify(before),
      JSON.stringify(after),
      "UPDATE_SETTINGS_PREFERENCES",
      userId,
      resolve,
      (error) =>
        reject(
          error instanceof Error
            ? error
            : new Error(error?.errors?.[0]?.message || "Audit log request failed")
        )
    );
  });

export const saveTaskManagerSettings = async ({
  dashboardId,
  userId,
  settings,
  existingConfig,
  dashboard,
}) => {
  const {
    statusMappings,
    digestConfig,
    emailMappings,
  } = normalizeTaskManagerSettingsPreferences(settings);

  const previousEmailMappings = existingConfig?.emailMappings || [];
  const previousPreferenceState = {
    statusMappings: Array.isArray(existingConfig?.statusMappings)
      ? existingConfig.statusMappings
      : [],
    digestConfig:
      existingConfig?.digestConfig && typeof existingConfig.digestConfig === "object"
        ? existingConfig.digestConfig
        : { enabled: false },
    emailMappings: previousEmailMappings.map((mapping) => ({
      alias: mapping.alias,
      emails: Array.isArray(mapping.emails) ? mapping.emails : [],
    })),
  };
  const nextPreferenceState = { statusMappings, digestConfig, emailMappings };
  const preferencesChanged =
    JSON.stringify(previousPreferenceState) !== JSON.stringify(nextPreferenceState);

  // Owners/viewers are audited by the RDS-backed dashboard update. Emit the
  // separate preference audit only when its own before/after state changed.
  if (preferencesChanged) {
    await createTaskManagerSettingsAudit({
      dashboardId,
      userId,
      before: previousPreferenceState,
      after: nextPreferenceState,
    });
  }

  const dashboardName = dashboard?.name?.trim();
  if (!dashboardName) {
    throw new Error("Dashboard information is required to update owners and viewers.");
  }

  // Owners/viewers are dashboard configuration stored in RDS aliases. Reuse
  // updateDashboard so the Lambda transaction and before/after audit path are
  // identical to other dashboard configuration changes.
  const updatedDashboard = await updateDashboard({
    dashboard_id: Number(dashboardId),
    name: dashboardName,
    description: dashboard?.description || "",
    logo: dashboard?.logo || "",
    app_ids: Array.isArray(dashboard?.app_ids)
      ? dashboard.app_ids
      : Array.isArray(dashboard?.appIds)
        ? dashboard.appIds
        : [],
    owners: Array.isArray(settings?.owners) ? settings.owners : [],
    viewers: Array.isArray(settings?.viewers) ? settings.viewers : [],
  });
  if (updatedDashboard?.error) {
    throw new Error(updatedDashboard.error);
  }

  const previousByAlias = new Map(
    previousEmailMappings.map((mapping) => [mapping.alias.toLowerCase(), mapping])
  );
  const nextEmailMappings = emailMappings.map((mapping) => {
    const previous = previousByAlias.get(mapping.alias.toLowerCase());
    return {
      ...mapping,
      preferenceId: previous?.preferenceId || emailMappingPreferenceId(mapping.alias),
      exists: !!previous,
    };
  });
  const nextAliases = new Set(
    nextEmailMappings.map((mapping) => mapping.alias.toLowerCase())
  );

  if (preferencesChanged) {
    const operations = [
      upsertPreference({
        preferenceId: statusMappingPreferenceId(dashboardId),
        exists: !!existingConfig?.statusMappingExists,
        preferenceType: STATUS_MAPPING_PREFERENCE_TYPE,
        label: STATUS_MAPPING_PREFERENCE_TYPE,
        associationId: String(dashboardId),
        associationIdType: TASK_MANAGER_DASHBOARD_ASSOCIATION_TYPE,
        userId,
        information: statusMappings,
      }),
      upsertPreference({
        preferenceId: digestConfigPreferenceId(dashboardId),
        exists: !!existingConfig?.digestConfigExists,
        preferenceType: DIGEST_CONFIG_PREFERENCE_TYPE,
        label: DIGEST_CONFIG_PREFERENCE_TYPE,
        associationId: String(dashboardId),
        associationIdType: TASK_MANAGER_DASHBOARD_ASSOCIATION_TYPE,
        userId,
        information: digestConfig,
      }),
      ...nextEmailMappings.map((mapping) =>
        upsertPreference({
          preferenceId: mapping.preferenceId,
          exists: mapping.exists,
          preferenceType: EMAIL_MAPPING_PREFERENCE_TYPE,
          label: EMAIL_MAPPING_PREFERENCE_TYPE,
          associationId: mapping.alias,
          associationIdType: EMAIL_MAPPING_ASSOCIATION_TYPE,
          userId,
          information: { emails: mapping.emails },
        })
      ),
      ...previousEmailMappings
        .filter((mapping) => !nextAliases.has(mapping.alias.toLowerCase()))
        .map((mapping) => removePreference(mapping.preferenceId)),
    ];

    await Promise.all(operations);
  }
  return {
    statusMappingExists: true,
    statusMappings,
    digestConfigExists: true,
    digestConfig,
    emailMappings: nextEmailMappings.map(({ preferenceId, alias, emails }) => ({
      preferenceId,
      alias,
      emails,
    })),
    owners: Array.isArray(updatedDashboard?.owners) ? updatedDashboard.owners : [],
    viewers: Array.isArray(updatedDashboard?.viewers) ? updatedDashboard.viewers : [],
    dashboard: updatedDashboard,
  };
};

/* ---- Logo upload / download via Vault ---- */

const VAULT_SERVICE = "TASK_MANAGER";
// Maps browser MIME types -> the extension Vault stores (also its content_type arg).
const LOGO_MIME_TO_EXT = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/svg+xml": "svg",
  "image/gif": "gif",
};

const parseVaultResult = (response, field) => {
  const raw = response?.data ? response.data[field] : null;
  if (raw == null) return null;
  return typeof raw === "string" ? JSON.parse(raw) : raw;
};

/**
 * Upload a logo through Vault and return the stored S3 object key.
 *
 * Flow: ask Vault for a presigned PUT URL (scoped to the generic bucket +
 * task_manager/ prefix, admin-gated) -> PUT the file to S3 -> return objectKey,
 * which the caller persists as the dashboard `logo`. Download/display resolves
 * the key back to a short-lived URL via getLogoViewUrl (also Vault).
 *
 * @param {File} file
 * @returns {Promise<string>} the Vault object key to store as the dashboard logo
 */
export const uploadLogo = async (file) => {
  const ext = LOGO_MIME_TO_EXT[file.type];
  if (!ext) {
    throw new Error("Unsupported logo type. Use PNG, JPG, SVG or GIF.");
  }
  const fileId = (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const filePath = `task_manager/${fileId}.${ext}`;

  const query = `mutation UploadTaskManagerLogo($service: String!, $content_type: String!, $service_params: AWSJSON!) {
    uploadDocumentToVault(service: $service, content_type: $content_type, service_params: $service_params) {
      uploadUrl
      objectKey
    }
  }`;
  const response = await callPlusGraphQLDirect(query, {
    service: VAULT_SERVICE,
    content_type: ext,
    service_params: JSON.stringify({ file_path: filePath, tagging: "" }),
  });
  const result = parseVaultResult(response, "uploadDocumentToVault");
  if (!result || !result.uploadUrl) {
    throw new Error("Could not get upload URL");
  }

  // Vault signs the PUT with ContentType "application/<ext>"; the header must match.
  const putResponse = await fetch(result.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": `application/${ext}` },
    body: file,
  });
  if (!putResponse.ok) {
    throw new Error("Logo upload failed");
  }
  return result.objectKey;
};

/**
 * Resolve a stored logo object key to a short-lived viewable URL via Vault.
 * @param {string} objectKey - the dashboard `logo` value
 * @returns {Promise<string|null>} a presigned GET URL, or null
 */
export const getLogoViewUrl = async (objectKey) => {
  if (!objectKey) return null;
  const ext = objectKey.split(".").pop();
  const query = `query GetTaskManagerLogo($service: String!, $content_type: String!, $service_params: AWSJSON!) {
    getDocumentFromVault(service: $service, content_type: $content_type, service_params: $service_params) {
      fileUrl
    }
  }`;
  const response = await callPlusGraphQLDirect(query, {
    service: VAULT_SERVICE,
    content_type: ext,
    service_params: JSON.stringify({ file_path: objectKey }),
  });
  const result = parseVaultResult(response, "getDocumentFromVault");
  return result ? result.fileUrl : null;
};

export const manageTabs = (input) => runTaskManagerOp("manageTabs", true, input);

export const manageStatusMapping = (input) => runTaskManagerOp("manageStatusMapping", true, input);

export const manageServiceAttributes = (input) => runTaskManagerOp("manageServiceAttributes", true, input);

export const updateNotifyConfig = (input) => runTaskManagerOp("updateNotifyConfig", true, input);

/**
 * Fetch all apps for the "Include Apps" multiselect (id + name only).
 *
 * Delegates to the shared `fetchApps` helper, which already loads apps once and
 * caches them locally — so we reuse that cache instead of hitting an endpoint
 * from the Task Manager feature.
 * @returns {Promise<Array<{ app_id: string, app_name: string }>>}
 */
export const fetchAllApps = async () => {
  const apps = await fetchApps();
  return Array.isArray(apps) ? apps : [];
};
